import { useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { normalizeAdSearchChunk } from '@/components/AdsWithInfiniteScroll/AdSearchChunk.helpers';
import { AdOrder, GetAdSearchResponse } from '@/api/types/ads.types';
import { Ad, PlatformSource } from '@/api/types/common.types';
import { GET_AD_SEARCH } from '@/api/queries/ads.queries';
import { mergeAdSearchResults } from '@/utils/helpers';

const EMPTY_ADS: Ad[] = [];

export interface LatestAdsQueryVariables {
  orderType: AdOrder | undefined;
  first: AdsChunkSize;
  keywords: string | undefined;
  filters: Record<string, unknown>;
  keywordsSource?: string;
  sourceLink?: string;
  source: PlatformSource;
}

interface UseLatestAdsQueryOptions {
  variables: LatestAdsQueryVariables;
  chunkSize: AdsChunkSize;
  skip: boolean;
}

export function useLatestAdsQuery({
  variables,
  chunkSize,
  skip,
}: UseLatestAdsQueryOptions) {
  const { data, loading, refetch, fetchMore, updateQuery } =
    useQuery<GetAdSearchResponse>(GET_AD_SEARCH, {
      variables,
      notifyOnNetworkStatusChange: true,
      skip,
      onCompleted: completedData =>
        normalizeAdSearchChunk(completedData, chunkSize, updateQuery),
    });

  const searchResult = data?.adSearch;
  const connection = searchResult?.ads;
  const nodes = connection?.nodes ?? EMPTY_ADS;
  const endCursor = connection?.pageInfo?.endCursor;
  const hasNextPage = connection?.pageInfo?.hasNextPage ?? false;
  const keywordsAds = searchResult?.keywordsAds ?? EMPTY_ADS;
  const isLoading = loading && !data;

  const refetchAndNormalize = useCallback(
    () =>
      refetch().then(({ data: refetched }) =>
        normalizeAdSearchChunk(refetched, chunkSize, updateQuery)
      ),
    [refetch, chunkSize, updateQuery]
  );

  const fetchMoreAds = useCallback(
    (after?: string) =>
      fetchMore({
        variables: after ? { after } : undefined,
        updateQuery: (prevResult, { fetchMoreResult }) =>
          mergeAdSearchResults(prevResult, fetchMoreResult, { chunkSize }),
      }),
    [fetchMore, chunkSize]
  );

  return {
    nodes,
    endCursor,
    hasNextPage,
    isLoading,
    refetchAndNormalize,
    fetchMoreAds,
    keywordsAds,
  };
}
